import {
  ErrorResponse,
  FileList,
  FileObject,
  FilesApi,
  FindProgramFilesRequest,
} from '@citruscamps/citrus-client'
import { Dispatch, useState } from 'react'
import {
  FetchNextPageOptions,
  InfiniteQueryObserverResult,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from 'react-query'
import {
  DefaultDozenPagination,
  DefaultMainPagination,
  UnlimitedPagination,
} from '../../../constants/pagination'
import { useRequestHandler } from '../../../hooks/useRequestHandler'
import { useSearchValue } from '../../../hooks/useSearchValue'
import { Pagination } from '../../../interfaces/pagination'
import { generateApiConfig } from '../../../utils/client-config'
import { generateItemKey, generateListKey } from '../../../utils/key-generator'

const DefaultFetchProps: FileFetchProps = {
  sort: 'created',
  order: 'DESC',
  filter: {},
}

export interface FileFetchProps {
  sort?: string
  order?: 'ASC' | 'DESC'
  filter?: Omit<FindProgramFilesRequest, 'program_id' | 'limit' | 'skip' | 'order' | 'sort'>
}

interface IProps {
  programId?: string
  pagination?: Pagination
  fetchProps?: FileFetchProps
  enabled?: boolean
}

interface FetchFiles {
  data: FileObject[]
  error?: ErrorResponse
  fetchProps: FileFetchProps
  isError: boolean
  isLoading: boolean
  pagination: Pagination
  search: string
  setPagination: Dispatch<Pagination>
  setSearch: Dispatch<string>
}

export const useFetchFiles = ({
  programId,
  pagination: initialPagination,
  fetchProps = DefaultFetchProps,
  enabled = true,
}: IProps): FetchFiles => {
  const { requestHandler } = useRequestHandler()
  const [pagination, setPagination] = useState<Pagination>(
    initialPagination || DefaultDozenPagination,
  )
  const { search, deferSearch, setSearch, isSearching } = useSearchValue('', {
    onDefer: () => setPagination(initialPagination || DefaultDozenPagination),
  })
  const queryKeys = generateListKey({
    type: 'file',
    program_id: programId,
    pagination,
    sort: fetchProps.sort,
    order: fetchProps.order,
    query: fetchProps.filter,
    search: deferSearch,
  })
  const {
    isLoading,
    isError,
    data: resp,
    error,
  } = useQuery<FileList, ErrorResponse>(
    queryKeys,
    async ({ signal }) => {
      if (!programId) {
        throw new Error('Unable to fetch data')
      }
      const client = new FilesApi(generateApiConfig())
      const skip = pagination.limit ? pagination.page * pagination.limit : undefined
      return await requestHandler<FileList>(() =>
        client.findProgramFiles(
          {
            program_id: programId,
            limit: pagination.limit,
            skip,
            order: fetchProps.order,
            sort: fetchProps.sort,
            search: deferSearch,
            filetype:
              fetchProps.filter?.filetype && fetchProps.filter?.filetype.length > 0
                ? fetchProps.filter.filetype
                : undefined,
            purpose:
              fetchProps.filter?.purpose && fetchProps.filter?.purpose.length > 0
                ? fetchProps.filter.purpose
                : undefined,
            id:
              fetchProps.filter?.id && fetchProps.filter?.id.length > 0
                ? fetchProps.filter.id
                : undefined,
          },
          { signal },
        ),
      )
    },
    {
      enabled: enabled && !!programId,
    },
  )

  return {
    data: resp?.data || [],
    error: error || undefined,
    fetchProps,
    isError,
    isLoading: isLoading || isSearching,
    pagination: {
      ...pagination,
      count: resp?.count || pagination.count,
      total: resp?.total || pagination.total,
      page: resp?.page || pagination.page,
      page_count: resp?.page_count || pagination.page_count,
    },
    search,
    setPagination,
    setSearch,
  }
}

interface IInfiniteProps extends IProps {
  fetchAll?: boolean
}

interface FetchInfiniteFiles extends Omit<FetchFiles, 'setPagination'> {
  isFetching: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  fetchNextPage: (
    options?: FetchNextPageOptions | undefined,
  ) => Promise<InfiniteQueryObserverResult<FileList, ErrorResponse>>
}

export const useFetchInfiniteFiles = ({
  programId,
  pagination = DefaultMainPagination,
  fetchAll = false,
  enabled = true,
  fetchProps = DefaultFetchProps,
}: IInfiniteProps): FetchInfiniteFiles => {
  const { requestHandler } = useRequestHandler()
  const queryClient = useQueryClient()
  const { search, deferSearch, setSearch, isSearching } = useSearchValue('')
  const queryKeys = generateListKey({
    type: 'file',
    pagination: UnlimitedPagination,
    sort: fetchProps.sort,
    order: fetchProps.order,
    program_id: programId,
    query: fetchProps,
    search: deferSearch,
  })
  const {
    data: resp,
    error,
    isError,
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage = false,
  } = useInfiniteQuery<FileList, ErrorResponse>(
    queryKeys,
    async ({ signal, pageParam: pg }) => {
      pg = pg || pagination
      if (!programId) {
        throw new Error('Unable to fetch data')
      }
      const client = new FilesApi(generateApiConfig())
      const skip = pg.limit ? pg.page * pg.limit : undefined
      const response = await requestHandler<FileList>(() =>
        client.findProgramFiles(
          {
            program_id: programId,
            limit: pg.limit,
            skip,
            order: fetchProps.order,
            sort: fetchProps.sort,
            search: deferSearch || undefined,
            filetype:
              fetchProps.filter?.filetype && fetchProps.filter?.filetype.length > 0
                ? fetchProps.filter.filetype
                : undefined,
            purpose:
              fetchProps.filter?.purpose && fetchProps.filter?.purpose.length > 0
                ? fetchProps.filter.purpose
                : undefined,
            id:
              fetchProps.filter?.id && fetchProps.filter?.id.length > 0
                ? fetchProps.filter.id
                : undefined,
          },
          { signal },
        ),
      )
      response.data.forEach((item) =>
        queryClient.setQueryData(
          generateItemKey({
            type: 'file',
            program_id: programId,
            id: item.id,
          }),
          item,
        ),
      )
      return response
    },
    {
      getNextPageParam: (resp: FileList): Pagination | undefined => {
        if (!resp.page_count || resp.page < resp.page_count - 1) {
          return {
            ...pagination,
            count: resp?.count,
            total: resp?.total,
            page: resp?.page + 1,
            page_count: resp?.page_count,
          }
        }
        return undefined
      },
      getPreviousPageParam: (resp: FileList): Pagination | undefined => {
        if (!resp.page_count || resp.page <= resp.page_count) {
          return {
            ...pagination,
            count: resp?.count,
            total: resp?.total,
            page: resp?.page - 1,
            page_count: resp?.page_count,
          }
        }
        return undefined
      },
      enabled: !!programId && !!enabled,
    },
  )

  if (!(isFetching || isFetchingNextPage || isLoading) && hasNextPage && fetchAll) {
    fetchNextPage()
  }

  const response = [...(resp?.pages || [])].pop()

  return {
    data: (resp?.pages || []).reduce<FileObject[]>((list, p) => [...list, ...p.data], []) || [],
    error: error || undefined,
    fetchProps,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isLoading: isLoading || isSearching,
    pagination: {
      ...pagination,
      count: response?.count || pagination.count,
      total: response?.total || pagination.total,
      page: response?.page || pagination.page,
      page_count: response?.page_count || pagination.page_count,
    },
    search,
    fetchNextPage,
    setSearch,
  }
}
